import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { entitlementService, Entitlements, TierName } from '../services/entitlementService';
import { useAuth } from './AuthContext';

interface EntitlementContextType {
  entitlements: Entitlements | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  // Helper methods for common checks
  canAccessEmail: () => boolean;
  canAccessAutomation: () => boolean;
  canCustomizePipeline: () => boolean;
  canAccessPortalr: () => boolean;
  getUserLimit: () => number;
  getTier: () => TierName | null;
  getPlanCode: () => string | null;
}

const EntitlementContext = createContext<EntitlementContextType | undefined>(undefined);

export const useEntitlements = () => {
  const context = useContext(EntitlementContext);
  if (context === undefined) {
    throw new Error('useEntitlements must be used within an EntitlementProvider');
  }
  return context;
};

interface EntitlementProviderProps {
  children: ReactNode;
}

export const EntitlementProvider: React.FC<EntitlementProviderProps> = ({ children }) => {
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const fetchEntitlements = useCallback(async () => {
    if (!isAuthenticated) {
      setEntitlements(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await entitlementService.getEntitlements();
      setEntitlements(data);
    } catch (err) {
      console.error('Failed to fetch entitlements:', err);
      setError(err instanceof Error ? err.message : 'Failed to load entitlements');
      // Set default BASIC entitlements on error
      setEntitlements({
        tier: 'BASIC',
        max_users: 3,
        email_enabled: false,
        email_monthly_limit: 0,
        custom_pipeline: false,
        automation_enabled: false,
        email_verified: false,
        trust_state: 'normal',
        high_risk_actions_allowed: false,
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch entitlements when auth state changes
  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  // Helper methods
  const canAccessEmail = useCallback(() => {
    return entitlements?.email_enabled ?? false;
  }, [entitlements]);

  const canAccessAutomation = useCallback(() => {
    return entitlements?.automation_enabled ?? false;
  }, [entitlements]);

  const canCustomizePipeline = useCallback(() => {
    return entitlements?.custom_pipeline ?? false;
  }, [entitlements]);

  const canAccessPortalr = useCallback(() => {
    return entitlements?.includes_portalr ?? false;
  }, [entitlements]);

  const getPlanCode = useCallback(() => {
    return entitlements?.plan_code ?? null;
  }, [entitlements]);

  const getUserLimit = useCallback(() => {
    return entitlements?.max_users ?? 3;
  }, [entitlements]);

  const getTier = useCallback(() => {
    return entitlements?.tier ?? null;
  }, [entitlements]);

  const value: EntitlementContextType = {
    entitlements,
    loading,
    error,
    refetch: fetchEntitlements,
    canAccessEmail,
    canAccessAutomation,
    canCustomizePipeline,
    canAccessPortalr,
    getUserLimit,
    getTier,
    getPlanCode,
  };

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
};
